<script>
import Spinner from 'shared/components/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useAgentsList } from 'dashboard/composables/useAgentsList';

import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import PanelAddButton from './sidebar/PanelAddButton.vue';
import PanelEmptyState from './sidebar/PanelEmptyState.vue';

/**
 * Participantes da conversa.
 *
 * Desde a #1243 a seção mostra a LISTA de quem participa, e não uma pilha de
 * avatares com a contagem ao lado: a contagem já é o `meta` do título da seção,
 * e avatar sem nome não diz quem é. O botão de somar gente é a moldura
 * tracejada embaixo da lista — a mesma do vazio, para que a seção vazia e a
 * cheia não pareçam duas telas diferentes.
 */
export default {
  components: {
    PanelAddButton,
    Spinner,
    MultiselectDropdownItems,
    PanelEmptyState,
    Thumbnail,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList(false);
    return {
      agentsList,
    };
  },
  data() {
    return {
      selectedWatchers: [],
      showDropDown: false,
    };
  },
  computed: {
    ...mapGetters({
      watchersUiFlas: 'conversationWatchers/getUIFlags',
      currentUser: 'getCurrentUser',
    }),
    watchersFromStore() {
      return this.$store.getters['conversationWatchers/getByConversationId'](
        this.conversationId
      );
    },
    // `left` e `viewport`: o painel é a última coluna da tela, e a dica nasce
    // fora dela quando aberta para cima ou para a direita — a mesma correção
    // dos ícones de redes sociais.
    removeTooltipOptions() {
      return {
        content: this.$t('CONVERSATION_PARTICIPANTS.REMOVE_PARTICIPANT'),
        placement: 'left',
        boundariesElement: 'viewport',
        container: 'body',
      };
    },
    watchersList: {
      get() {
        return this.selectedWatchers;
      },
      set(participants) {
        this.selectedWatchers = [...participants];
        const userIds = participants.map(el => el.id);
        this.updateParticipant(userIds);
      },
    },
  },
  watch: {
    conversationId() {
      this.fetchParticipants();
    },
    watchersFromStore(participants = []) {
      this.selectedWatchers = [...participants];
    },
  },
  mounted() {
    this.fetchParticipants();
    this.$store.dispatch('agents/get');
  },
  methods: {
    fetchParticipants() {
      const conversationId = this.conversationId;
      this.$store.dispatch('conversationWatchers/show', { conversationId });
    },
    async updateParticipant(userIds) {
      const conversationId = this.conversationId;
      let alertMessage = this.$t(
        'CONVERSATION_PARTICIPANTS.API.SUCCESS_MESSAGE'
      );

      try {
        await this.$store.dispatch('conversationWatchers/update', {
          conversationId,
          userIds,
        });
      } catch (error) {
        alertMessage =
          error?.message ||
          this.$t('CONVERSATION_PARTICIPANTS.API.ERROR_MESSAGE');
      } finally {
        useAlert(alertMessage);
      }
      this.fetchParticipants();
    },
    isCurrentUser(watcher) {
      return watcher.id === this.currentUser.id;
    },
    // Tirar um participante é o mesmo `update` com a lista sem ele — não existe
    // rota de remoção individual. O setter de `watchersList` já dispara o
    // PATCH e o alerta.
    onRemoveParticipant(watcher) {
      this.watchersList = this.watchersList.filter(
        participant => participant.id !== watcher.id
      );
    },
    onOpenDropdown() {
      this.showDropDown = true;
    },
    onCloseDropdown() {
      this.showDropDown = false;
    },
    onClickItem(agent) {
      const isAgentSelected = this.watchersList.some(
        participant => participant.id === agent.id
      );

      if (isAgentSelected) {
        const updatedList = this.watchersList.filter(
          participant => participant.id !== agent.id
        );

        this.watchersList = [...updatedList];
      } else {
        this.watchersList = [...this.watchersList, agent];
      }
    },
  },
};
</script>

<template>
  <!-- O `v-on-clickaway` mora AQUI, e nao no painel: o ouvinte da diretiva
       escuta o clique na fase de borbulha do documento, entao o mesmo clique
       que abre a expansao (no botao de somar, que e irmao do painel) chega em
       seguida ao documento como "clique fora do painel" e a fechava na hora --
       ela nunca abria. Na raiz, os dois gatilhos ficam dentro do elemento
       vigiado. E o mesmo arranjo do `LabelBox.vue`, inclusive o Esc. -->
  <div
    v-on-clickaway="
      () => {
        onCloseDropdown();
      }
    "
    class="relative bg-transparent"
    @keyup.esc="onCloseDropdown"
  >
    <Spinner v-if="watchersUiFlas.isFetching && !selectedWatchers.length" />

    <!-- Cartao do participante, medido na referencia da #1243: contorno
         cheio, fundo rebaixado, 8px/10px de respiro e 6px entre um e outro. -->
    <div v-else-if="selectedWatchers.length" class="flex flex-col gap-1.5">
      <div
        v-for="watcher in selectedWatchers"
        :key="watcher.id"
        class="flex items-center w-full gap-2 px-2.5 py-2 border rounded-lg group bg-surface-sunken border-line-faint"
      >
        <Thumbnail
          :src="watcher.thumbnail"
          size="22px"
          :username="watcher.name"
        />
        <!-- O "(você)" cola no nome, e não na borda direita do cartão: solto
             lá, ele parecia uma coluna de outra coisa, e agora divide o lugar
             com o botão de remover. -->
        <span class="flex items-center min-w-0 gap-1 text-xs text-body">
          <span class="truncate">{{ watcher.name }}</span>
          <span v-if="isCurrentUser(watcher)" class="shrink-0 text-muted">
            ({{ $t('CONVERSATION_PARTICIPANTS.YOU') }})
          </span>
        </span>
        <!-- Aparece ao passar o mouse, e TAMBÉM ao receber foco: `opacity` não
             tira o botão da ordem de Tab, então sem o `focus-visible` quem
             navega por teclado pararia num alvo invisível. Em tela sem mouse
             ele fica visível de saída (ver o `@media (hover: none)` abaixo) --
             `opacity: 0` esconde, mas não desarma: no toque o alvo continuava
             clicável, e remover participante por engano não tem desfazer. -->
        <button
          v-tooltip="removeTooltipOptions"
          type="button"
          :aria-label="$t('CONVERSATION_PARTICIPANTS.REMOVE_PARTICIPANT')"
          class="participant-remove flex items-center justify-center w-6 h-6 p-0 ml-auto transition-opacity duration-150 rounded-md opacity-0 shrink-0 text-muted rtl:ml-0 rtl:mr-auto group-hover:opacity-100 focus-visible:opacity-100 hover:text-ruby-800"
          @click="onRemoveParticipant(watcher)"
        >
          <fluent-icon icon="dismiss" size="14" />
        </button>
      </div>
      <PanelAddButton
        :label="$t('CONVERSATION_PARTICIPANTS.ADD_PARTICIPANT')"
        @click="onOpenDropdown"
      />
    </div>

    <PanelEmptyState
      v-else
      :message="$t('CONVERSATION_PARTICIPANTS.NO_PARTICIPANTS_TEXT')"
      :action-label="$t('CONVERSATION_PARTICIPANTS.ADD_PARTICIPANT')"
      @action="onOpenDropdown"
    />

    <div :class="{ 'dropdown-pane--open': showDropDown }" class="dropdown-pane">
      <div class="flex items-center justify-between mb-1">
        <h4
          class="m-0 overflow-hidden text-sm whitespace-nowrap text-ellipsis text-body"
        >
          {{ $t('CONVERSATION_PARTICIPANTS.ADD_PARTICIPANTS') }}
        </h4>
        <woot-button
          icon="dismiss"
          size="tiny"
          color-scheme="secondary"
          variant="clear"
          @click="onCloseDropdown"
        />
      </div>
      <MultiselectDropdownItems
        :options="agentsList"
        :selected-items="selectedWatchers"
        has-thumbnail
        @click="onClickItem"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
// `top-full`, e nao `top-8`: 2rem contava a partir do topo da SECAO, entao com
// dois ou mais participantes a expansao abria por cima dos nomes. O `full` e a
// altura do proprio bloco -- ou seja, logo abaixo do botao, que e o ultimo
// elemento dele nos dois estados (com lista e no vazio).
.dropdown-pane {
  @apply box-border w-full top-full mt-1;
}

// Sem mouse nao existe `:hover`, entao o botao ficava invisivel e clicavel ao
// mesmo tempo -- o pior dos dois mundos num alvo destrutivo. Mostrar e a mesma
// saida que a alca de arrastar do `PanelSection.vue` ja usa.
@media (hover: none) {
  .participant-remove {
    opacity: 1;
  }
}
</style>
